import { Box } from '@mantine/core';
import { useDi } from '@root/Services/DI';
import { useEventValue } from '@root/Services/EventEmitter';
import { FormatService, NamedFormats } from '@root/Services/FormatService';
import { useNav } from '@root/Services/NavigationService';
import { useCallback, useMemo } from 'react';
import { useShowbackUserPrefs } from '../Common';
import { ActionList, ActionListItem } from './Components';
import { ShowbackSummaryModel } from './ShowbackSummaryModel';

export function SummaryStatList({ summaryModel }: { summaryModel: ShowbackSummaryModel }) {
    const summaryData = useEventValue(summaryModel.dataChanged);

    const loading = useEventValue(summaryModel.loading);
    const fmtSvc = useDi(FormatService);
    const sbModel = summaryModel.showbackModel;
    const moneyFormat = useShowbackUserPrefs().format;

    const nav = useNav();
    const openReconciliation = useCallback(() => sbModel.openTab(nav, 'reconciliation'), [sbModel, nav]);
    const openUntaggedEditor = useCallback(() => sbModel.openRuleEditor(nav, undefined, 'UntaggedCosts'), [sbModel, nav]);
    const openDiscountEditor = useCallback(() => sbModel.openRuleEditor(nav, undefined, 'Discounts', 'commitments'), [sbModel, nav]);
    const openSharedCostEditor = useCallback((subType?: string) => sbModel.openRuleEditor(nav, undefined, 'SharedCosts', subType), [sbModel, nav]);
    const openCreditsEditor = useCallback(() => openSharedCostEditor('credit'), [openSharedCostEditor]);
    const openSupportEditor = useCallback(() => openSharedCostEditor('support'), [openSharedCostEditor]);
    const openFeesEditor = useCallback(() => openSharedCostEditor('fee'), [openSharedCostEditor]);
    const openMktplaceEditor = useCallback(() => sbModel.openRuleEditor(nav, undefined, 'Marketplace'), [sbModel, nav]);
    const openAdvancedEditor = useCallback(() => sbModel.openRuleEditor(nav, undefined, 'Advanced'), [sbModel, nav]);

    const invoiceListItems = useMemo(() => {
        const metrics = { ...summaryData?.stats };
        const val = (...values: (undefined | number)[]) => {
            const amount = values.reduce((r, value) => (r ?? 0) + (value ?? 0), 0);
            return amount === undefined ? '—' : fmtSvc.format(moneyFormat ?? 'money-whole', amount);
        };
        const isSignificant = (...values: (undefined | number)[]) => {
            return values.some((value) => Math.abs(value ?? 0) > 0.01);
        };
        const { allocated: alloc, unallocated: unalloc } = metrics;
        const GetUnallocatedChildren = () => {
            const returnList = [];
            if (isSignificant(metrics.unallocatedUntagged)) {
                returnList.push({
                    name: 'Untagged Costs',
                    metric: val(metrics.unallocatedUntagged),
                    action: 'Allocate',
                    onAction: openUntaggedEditor,
                });
            }
            if (isSignificant(metrics.discountSp, metrics.discountRi)) {
                returnList.push({
                    name: 'Discounts',
                    metric: val(metrics.discountSp, metrics.discountRi),
                    action: 'Allocate',
                    onAction: openDiscountEditor,
                });
            }
            if (isSignificant(metrics.discountCredits, metrics.discountEdp, metrics.discountPvtRate)) {
                returnList.push({
                    name: 'Credits',
                    metric: val(metrics.discountCredits, metrics.discountEdp, metrics.discountPvtRate),
                    action: 'Allocate',
                    onAction: openCreditsEditor,
                });
            }
            if (isSignificant(metrics.chargeSupport)) {
                returnList.push({
                    name: 'Support',
                    metric: val(metrics.chargeSupport),
                    action: 'Allocate',
                    onAction: sbModel.availabilityInfo?.hasAws ? openSupportEditor : openAdvancedEditor,
                });
            }
            if (isSignificant(metrics.chargeFees)) {
                returnList.push({
                    name: 'Fees',
                    metric: val(metrics.chargeFees),
                    action: 'Allocate',
                    onAction: sbModel.availabilityInfo?.hasAws ? openFeesEditor : openAdvancedEditor,
                });
            }
            if (isSignificant(metrics.chargeMarketplace)) {
                returnList.push({
                    name: 'Marketplace Charges',
                    metric: val(metrics.chargeMarketplace),
                    action: 'Allocate',
                    onAction: sbModel.availabilityInfo?.hasAws ? openMktplaceEditor : openAdvancedEditor,
                });
            }

            if (isSignificant(metrics.unallocatedNoCategory)) {
                returnList.push({ name: 'Other', metric: val(metrics.discountCredits), action: 'Allocate', onAction: openAdvancedEditor });
            }

            return returnList;
        };

        const getAmortizedSection = () => {
            const returnList = [];
            if (isSignificant(metrics.upfrontSpFees)) {
                returnList.push({ name: 'Savings Plans', metric: val(metrics.upfrontSpFees) });
            }
            if (isSignificant(metrics.upfrontRiFees)) {
                returnList.push({ name: 'Reserved Instances', metric: val(metrics.upfrontRiFees) });
            }
            if (returnList.length > 0) {
                returnList.unshift({ title: 'Amortization of Up-Front Payments', metric: val(metrics.upfrontSpFees, metrics.upfrontRiFees) });
                returnList.push({ spacer: true });
            }
            return returnList;
        };

        return [
            { title: 'Invoice Charges', metric: val(metrics.total) },
            { section: 'Allocated', action: 'Reconcile', onAction: openReconciliation, metric: val(alloc), children: [] },
            {
                section: 'Unallocated',
                metric: val(unalloc),
                initExpanded: true,
                children: GetUnallocatedChildren(),
            },
            { spacer: true },
            ...getAmortizedSection(),
            { total: 'Total Showback Amounts', metric: val(alloc, unalloc, metrics.upfrontSpFees, metrics.upfrontRiFees) },
        ] as ActionListItem[];
    }, [summaryData, moneyFormat]);

    return (
        <Box sx={{ transition: 'all .5s', filter: loading ? 'blur(5px)' : 'blur(0)' }}>
            <ActionList items={invoiceListItems} />
        </Box>
    );
}
